import { ErrorCode, PlayerError } from '@tunein/web-media-player';
import { pick } from 'es-toolkit/compat';
import { api } from '../../singletons';
import { ERROR_CODES } from '../AdReporter/constants';
import { AD_MEDIUM_TYPES, AD_SLOTS, clientAdTypes } from '../constants';
export class ClientSideAdReporter {
    #adEvent;
    #sessionMetadata;
    #didReportError = false;
    #lastErrorMessage;
    #lastErrorCode;
    set sessionMetadata(sessionMetadata) {
        this.#sessionMetadata = sessionMetadata;
        this.#didReportError = false;
        this.#lastErrorMessage = '';
        this.#lastErrorCode = '';
    }
    #getAdSlot(adType) {
        const clientAdType = adType || this.#sessionMetadata?.clientType;
        switch (clientAdType) {
            case clientAdTypes.preroll:
                return AD_SLOTS.PREROLL;
            case clientAdTypes.midroll:
                return AD_SLOTS.MIDROLL;
            default:
                return AD_SLOTS.UNSPECIFIED;
        }
    }
    #getAdMediumType(medium) {
        const adMedium = medium || this.#sessionMetadata?.medium;
        switch (adMedium) {
            case 'video':
                return AD_MEDIUM_TYPES.VIDEO;
            case 'audio':
                return AD_MEDIUM_TYPES.AUDIO;
            default:
                return AD_MEDIUM_TYPES.UNSPECIFIED;
        }
    }
    #getAdParameters(adEvent) {
        if (adEvent) {
            this.#adEvent = adEvent;
        }
        const { requestId, bitrate, totalAds, creativeId, adPosition = 1, destinationUrl, } = this.#adEvent || {};
        const { adUnitId, adNetworkName, adRequestId, wasAdSkipped, adCount = 0, maxRolls, maxTotalRolls, wasAdCancelled = false, } = this.#sessionMetadata || {};
        // adPosition is 1-based counter from adEvent
        // adCount is 0-based ad counter from AdPlayer (contains successfuly played video/audio ads)
        // because adPosition is 1-based we distract it by 1 to get the 0-based index
        const adPositionIndex = adCount + adPosition - 1;
        return {
            adUnitId: adUnitId || '',
            adNetworkName: adNetworkName || '',
            adRequestId: requestId || adRequestId || 'none',
            adSlot: this.#getAdSlot(),
            adType: this.#getAdMediumType(),
            adRequestDefaultTimer: true,
            midrollTimerInSeconds: 0,
            bitrate: bitrate || 0,
            noOfVideoaudiorollsPlayed: adPositionIndex + 1,
            noOfVideoaudiorollsReceived: totalAds || 1,
            adCreativeId: creativeId || '',
            currentVideoaudiorollIdx: adPositionIndex,
            wasAdSkipped: wasAdSkipped || false,
            maxRolls: maxRolls || 0,
            maxTotalRolls: maxTotalRolls || 0,
            isStreamPrebuffered: false,
            prebufferTimeMs: 0,
            props: {},
            wasAdCancelled,
            destinationUrl: destinationUrl || '',
        };
    }
    #getAdErrorDescription(errorCode, isAdRequestError) {
        switch (errorCode) {
            case ERROR_CODES.VAST_EMPTY_RESPONSE:
            case ERROR_CODES.VAST_NO_ADS_AFTER_WRAPPER:
                return 'Audio Video Ad, Request Failed';
            case ERROR_CODES.VAST_LOAD_TIMEOUT:
                return 'Audio Video Ad, Playback Timeout';
            case ErrorCode.AdNoFillError:
                return 'Audio Video Ad, No Fill';
            case ErrorCode.RequestTimeout:
                return 'Audio Video Ad, Request Timeout';
            case ErrorCode.AdSDKLoadError:
            case ErrorCode.AdSDKNotLoaded:
                return 'Audio Video Ad, SDK Load Failed';
            case ErrorCode.AdNoConfigError:
                return 'Audio Video Ad, Missing Ad Config';
            default:
                return isAdRequestError
                    ? 'Audio Video Ad, Unknown Ad Request Error'
                    : 'Audio Video Ad, Unknown Ad Playback Error';
        }
    }
    onAdSkipped() {
        if (this.#sessionMetadata) {
            this.#sessionMetadata.wasAdSkipped = true;
        }
    }
    reportAdLoaded(adEvent) {
        this.#adEvent = adEvent;
        api.telemetry.reportAdsVideoAudioRollResponseReceived(pick(this.#getAdParameters(), [
            'adUnitId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'adRequestDefaultTimer',
            'midrollTimerInSeconds',
            'bitrate',
            'noOfVideoaudiorollsReceived',
        ]));
    }
    reportAdStart(adEvent) {
        api.telemetry.reportAdsPlaybackStarted(pick(this.#getAdParameters(adEvent), [
            'adUnitId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'adRequestDefaultTimer',
            'midrollTimerInSeconds',
            'bitrate',
            'noOfVideoaudiorollsReceived',
            'adCreativeId',
            'currentVideoaudiorollIdx',
            'isStreamPrebuffered',
            'prebufferTimeMs',
            'props',
        ]));
    }
    reportAdResume(adEvent) {
        api.telemetry.reportAdsPlaybackResumed(pick(this.#getAdParameters(adEvent), [
            'adUnitId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'noOfVideoaudiorollsReceived',
            'adCreativeId',
            'currentVideoaudiorollIdx',
            'isStreamPrebuffered',
            'prebufferTimeMs',
            'props',
        ]));
    }
    reportAdPause(adEvent) {
        api.telemetry.reportAdsPlaybackPaused({
            ...pick(this.#getAdParameters(adEvent), [
                'adUnitId',
                'adNetworkName',
                'adRequestId',
                'adSlot',
                'adType',
                'noOfVideoaudiorollsReceived',
                'adCreativeId',
                'currentVideoaudiorollIdx',
                'isStreamPrebuffered',
                'prebufferTimeMs',
                'props',
            ]),
            debugDescription: 'Audio Video Ad, Playback Paused',
        });
    }
    reportAdComplete() {
        api.telemetry.reportAdsPlaybackFinished(pick(this.#getAdParameters(), [
            'adUnitId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'adRequestDefaultTimer',
            'midrollTimerInSeconds',
            'noOfVideoaudiorollsReceived',
            'adCreativeId',
            'currentVideoaudiorollIdx',
            'wasAdSkipped',
            'isStreamPrebuffered',
            'prebufferTimeMs',
            'props',
            'wasAdCancelled',
        ]));
    }
    reportAdBreakCompleted() {
        api.telemetry.reportAdsVideoAudioRollCompleted(pick(this.#getAdParameters(), [
            'adCreativeId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'adUnitId',
            'currentVideoaudiorollIdx',
            'noOfVideoaudiorollsReceived',
            'noOfVideoaudiorollsPlayed',
        ]));
    }
    reportAdRequested(hasApsKeywords) {
        api.telemetry.reportAdsVideoAudioRollRequested({
            ...pick(this.#getAdParameters(), [
                'adUnitId',
                'adNetworkName',
                'adRequestId',
                'adSlot',
                'adType',
                'adRequestDefaultTimer',
                'midrollTimerInSeconds',
                'maxRolls',
                'maxTotalRolls',
            ]),
            adRequestHasAmazonKeywords: hasApsKeywords,
            biddingNetworks: [],
        });
    }
    reportAdsBiddingRequested() {
        const { adUnitId, adNetworkName, adRequestId } = this.#sessionMetadata || {};
        api.telemetry.reportAdsBiddingRequested({
            adRequestId: adRequestId || '',
            adUnitId: adUnitId || '',
            adNetworkName: adNetworkName || 'dfp',
            adType: AD_MEDIUM_TYPES.VIDEO,
            adSlot: AD_SLOTS.PREROLL,
            adSize: '0x0',
        });
    }
    reportAdsBiddingResponseReceived() {
        const { adUnitId, adNetworkName, adRequestId } = this.#sessionMetadata || {};
        api.telemetry.reportAdsBiddingResponseReceived({
            adRequestId: adRequestId || '',
            adUnitId: adUnitId || '',
            adNetworkName: adNetworkName || 'dfp',
            adType: AD_MEDIUM_TYPES.VIDEO,
            adSlot: AD_SLOTS.PREROLL,
            adSize: '0x0',
        });
    }
    reportAdsBiddingRequestFailed(errorCode, errorMessage) {
        // This call is done asynchronously to ad requests and in intervals
        const { adUnitId, adNetworkName, adRequestId } = this.#sessionMetadata || {};
        api.telemetry.reportAdsBiddingRequestFailed({
            adRequestId: adRequestId || '',
            adUnitId: adUnitId || '',
            adNetworkName: adNetworkName || 'dfp',
            isRequestCanceled: false,
            adType: AD_MEDIUM_TYPES.VIDEO,
            adSlot: AD_SLOTS.PREROLL,
            adSize: '0x0',
            errorCode,
            errorMessage,
            debugDescription: 'AmazonKeywords Error',
        });
    }
    reportAdClick() {
        api.telemetry.reportAdsVideoAudioRollClicked(pick(this.#getAdParameters(), [
            'adCreativeId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'adUnitId',
            'currentVideoaudiorollIdx',
            'noOfVideoaudiorollsReceived',
            'destinationUrl',
        ]));
    }
    reportEligibility({ medium, isEligible, isPlatformEligible, maxRolls, maxTotalRolls, }) {
        api.telemetry.reportAdsVideoAudioRollEligibilityDecided({
            adSlot: AD_SLOTS.PREROLL,
            adType: this.#getAdMediumType(medium),
            isPlatformEligible,
            isEligible,
            maxRolls,
            maxTotalRolls,
        });
    }
    reportPlaybackCancelled({ duration = 0, reason }) {
        if (this.#sessionMetadata) {
            this.#sessionMetadata.wasAdSkipped = true;
        }
        api.telemetry.reportAdsPlaybackCancelled({
            ...pick(this.#getAdParameters(), [
                'adCreativeId',
                'adNetworkName',
                'adRequestId',
                'adSlot',
                'adType',
                'adUnitId',
                'currentVideoaudiorollIdx',
                'noOfVideoaudiorollsReceived',
            ]),
            duration,
            reason,
            props: {},
        });
    }
    reportError(error) {
        if (this.#didReportError) {
            api.telemetry.logError({
                message: 'Client side ad break experienced extra error',
                context: {
                    errorMessage: error?.message,
                    errorCode: error?.errorCode || error?.code,
                    lastErrorMessage: this.#lastErrorMessage,
                    lastErrorCode: this.#lastErrorCode,
                },
            });
            return;
        }
        this.#didReportError = true;
        let errorCode = -1;
        let errorMessage = '';
        if (error.isAdError) {
            errorCode = error.errorCode || errorCode;
            errorMessage = error.message;
        }
        else if (PlayerError.isPlayerError(error)) {
            errorCode = error.code || errorCode;
            errorMessage = error.message;
        }
        // Used for logging error metadata when more than 1 error is detected for an ad break
        this.#lastErrorMessage = errorMessage;
        this.#lastErrorCode = errorCode?.toString();
        const isAdRequestError = [
            ERROR_CODES.VAST_EMPTY_RESPONSE,
            ERROR_CODES.VAST_NO_ADS_AFTER_WRAPPER,
            ERROR_CODES.FAILED_TO_REQUEST_ADS,
            ERROR_CODES.VAST_LOAD_TIMEOUT,
            ErrorCode.AdNoFillError,
            ErrorCode.AdSDKNotLoaded,
            ErrorCode.AdSDKLoadError,
            ErrorCode.AdNoConfigError,
            ErrorCode.RequestTimeout,
        ].includes(errorCode);
        const debugDescription = this.#getAdErrorDescription(errorCode, isAdRequestError);
        if (isAdRequestError) {
            api.telemetry.reportAdsVideoAudioRollRequestFailed({
                ...pick(this.#getAdParameters(), [
                    'adUnitId',
                    'adNetworkName',
                    'adRequestId',
                    'adSlot',
                    'adType',
                    'adRequestDefaultTimer',
                    'midrollTimerInSeconds',
                ]),
                errorCode: errorCode?.toString(),
                errorMessage: errorMessage,
                debugDescription,
            });
        }
        else {
            api.telemetry.reportAdsPlaybackFailed({
                ...pick(this.#getAdParameters(), [
                    'adUnitId',
                    'adNetworkName',
                    'adRequestId',
                    'adSlot',
                    'adType',
                    'adRequestDefaultTimer',
                    'midrollTimerInSeconds',
                    'bitrate',
                    'noOfVideoaudiorollsReceived',
                    'currentVideoaudiorollIdx',
                    'adCreativeId',
                    'isStreamPrebuffered',
                    'prebufferTimeMs',
                    'props',
                ]),
                errorCode: errorCode?.toString(),
                errorMessage: errorMessage,
                debugDescription,
            });
        }
    }
}
